import React, {useRef} from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import KeyboardInput from '/component/base/KeyboardInput';
import {Text} from '/component/base/Text/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';
import {
  selectCountries,
  selectUsStates,
} from '/redux/selector/remoteConfig.selector';

import {normalizeCountry} from '../../ManageMySite.constants';
import {BaseDropdown} from '../../widgets/BaseDropdown';
import {FieldWrapper} from '../../widgets/FieldWrapper';
import {SectionTitle} from '../../widgets/SectionTitle';

import {styles} from './AddressSection.style';
import type {AddressSectionProps} from './AddressSection.types';

const {addressSection} = ManageMySiteTestIDs;

export const AddressSection = ({
  country,
  initialCountry,
  street,
  city,
  state,
  zip,
  onCountryChange,
  onStreetChange,
  onCityChange,
  onStateChange,
  onZipChange,
  onZipBlur,
  hasSubmitted,
  errors,
}: AddressSectionProps) => {
  const streetInputRef = useRef<any>(null);
  const cityInputRef = useRef<any>(null);
  const zipInputRef = useRef<any>(null);

  const stateOptions = useSelector(selectUsStates);
  const allCountries = useSelector(selectCountries);

  const STREET_MAX_LENGTH = 200;
  const CITY_MAX_LENGTH = 100;

  const normalizedCountry = normalizeCountry(country) || 'US';
  const normalizedInitial = normalizeCountry(initialCountry) || 'US';
  const zipMaxLength = normalizedCountry === 'US' ? 10 : 15;

  const shouldUseReadonlyUsField = normalizedInitial === 'US';

  const usOption = allCountries.find(c => c.value === 'US') || {
    label: 'United States',
    value: 'US',
  };

  const legacyOption =
    normalizedInitial !== 'US'
      ? allCountries.find(c => c.value === normalizedInitial) || {
          label: normalizedInitial,
          value: normalizedInitial,
        }
      : null;

  const countryOptions =
    legacyOption && normalizedInitial !== 'US'
      ? [legacyOption, usOption]
      : [usOption];

  const selectedCountryLabel = countryOptions.find(
    i => i.value === normalizedCountry,
  )?.label;

  const selectedStateLabel = stateOptions.find(i => i.value === state)?.label;

  const showStateError = hasSubmitted && !!errors.state;
  const showZipError = hasSubmitted && !!errors.zip;

  const streetAccessibilityLabel = street
    ? `${street}, Street address`
    : 'Street address';

  const cityAccessibilityLabel = city ? `${city}, City` : 'City';

  const zipAccessibilityLabel = showZipError
    ? zip
      ? `${zip}, Zipcode, Error: ${errors.zip}`
      : `Zipcode, Error: ${errors.zip}`
    : zip
    ? `${zip}, Zipcode`
    : 'Zipcode';

  // const handleStreetPress = () => {
  //   streetInputRef.current?.focus();
  // };

  // const handleCityPress = () => {
  //   cityInputRef.current?.focus();
  // };

  // const handleZipPress = () => {
  //   zipInputRef.current?.focus();
  // };

  return (
    <View
      style={styles.section}
      testID={addressSection.container}
      accessible={false}>
      <SectionTitle
        title={tt('displayAddress', 'Display Address')}
        accessibilityRole="header"
      />

      <FieldWrapper
        label={String(tt('country', 'Country'))}
        labelTestID={addressSection.countryLabel}
        testID={addressSection.countryField}>
        {shouldUseReadonlyUsField ? (
          <View
            accessible
            focusable
            accessibilityLabel="Country, United States, disabled"
            accessibilityRole="text"
            accessibilityState={{disabled: true}}
            testID={addressSection.countryInput}
            style={styles.disabledInput}>
            <Text
              style={styles.disabledText}
              accessible={false}
              allowFontScaling
              maxFontSizeMultiplier={2}>
              United States
            </Text>
          </View>
        ) : (
          <KeyboardFocusable
            accessible
            accessibilityLabel={
              selectedCountryLabel
                ? `${selectedCountryLabel}, Country`
                : 'Country'
            }
            accessibilityHint="Double tap to change"
            accessibilityRole="none">
            <BaseDropdown
              value={normalizedCountry}
              onChange={onCountryChange}
              options={countryOptions}
              testID={addressSection.countryDropdown}
              accessible={false}
              placeholder={String(tt('enterCountry', 'Select country'))}
            />
          </KeyboardFocusable>
        )}
      </FieldWrapper>

      <FieldWrapper
        label={String(tt('street', 'Street'))}
        labelTestID={addressSection.streetLabel}
        testID={addressSection.streetField}>
        <KeyboardInput
          ref={streetInputRef}
          value={street}
          onChangeText={onStreetChange}
          style={[styles.input, styles.streetInput]}
          accessible
          accessibilityLabel={streetAccessibilityLabel}
          accessibilityHint="Double tap to edit"
          accessibilityRole="none"
          testID={addressSection.streetInput}
          placeholder={String(tt('enterStreet', 'Enter street address'))}
          placeholderTextColor="#9CA3AF"
          maxLength={STREET_MAX_LENGTH}
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </FieldWrapper>

      <FieldWrapper
        label={String(tt('city', 'City'))}
        labelTestID={addressSection.cityLabel}
        testID={addressSection.cityField}>
        <KeyboardInput
          ref={cityInputRef}
          value={city}
          onChangeText={onCityChange}
          style={styles.input}
          accessible
          accessibilityLabel={cityAccessibilityLabel}
          accessibilityHint="Double tap to edit"
          accessibilityRole="none"
          testID={addressSection.cityInput}
          placeholder={String(tt('enterCity', 'Enter city'))}
          placeholderTextColor="#9CA3AF"
          maxLength={CITY_MAX_LENGTH}
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </FieldWrapper>

      <FieldWrapper
        label={String(tt('state', 'State'))}
        labelTestID={addressSection.stateLabel}
        testID={addressSection.stateField}
        errorText={showStateError ? errors.state : undefined}
        errorTestID={addressSection.stateError}>
        <KeyboardFocusable
          accessible
          accessibilityLabel={
            showStateError
              ? selectedStateLabel
                ? `${selectedStateLabel}, State, Error: ${errors.state}`
                : `State, Error: ${errors.state}`
              : selectedStateLabel
              ? `${selectedStateLabel}, State`
              : 'State'
          }
          accessibilityHint="Double tap to change"
          accessibilityRole="none"
          accessibilityState={showStateError ? {invalid: true} : undefined}>
          <BaseDropdown
            dropdownKey="state"
            value={state}
            onChange={onStateChange}
            options={stateOptions}
            hasError={showStateError}
            errorText={errors.state}
            testID={addressSection.stateDropdown}
            accessible={false}
            placeholder={String(tt('enterState', 'Select state'))}
          />
        </KeyboardFocusable>
      </FieldWrapper>

      <FieldWrapper
        label={String(tt('zipcode', 'Zipcode'))}
        labelTestID={addressSection.zipLabel}
        testID={addressSection.zipField}
        errorText={showZipError ? errors.zip : undefined}
        errorTestID={addressSection.zipError}>
        <KeyboardInput
          ref={zipInputRef}
          value={zip}
          onChangeText={value => {
            const cleaned = value.replace(/[^0-9-]/g, '');
            onZipChange(cleaned);
          }}
          onBlur={onZipBlur}
          style={[styles.input, showZipError ? styles.inputError : null]}
          accessible
          accessibilityLabel={zipAccessibilityLabel}
          accessibilityHint="Double tap to edit"
          accessibilityRole="none"
          accessibilityState={showZipError ? {invalid: true} : undefined}
          testID={addressSection.zipInput}
          placeholderTextColor="#9CA3AF"
          placeholder={String(tt('enterZipcode', 'Enter ZIP code'))}
          keyboardType="numbers-and-punctuation"
          maxLength={zipMaxLength}
          allowFontScaling
          maxFontSizeMultiplier={2}
        />
      </FieldWrapper>
    </View>
  );
};
